import { Badge, Button, EmptyState, Skeleton, Table, useConfirm, useToast } from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { ApiClientError, type RoleListItem } from '@uchat-team-management/shared';
import { Show } from 'solid-js';
import { useDeleteRole, useRoles } from './hooks';

export const RolesTable = (props: { onEdit: (role: RoleListItem) => void }) => {
  const { session } = useAuth();
  const roles = useRoles();
  const remove = useDeleteRole();
  const toast = useToast();
  const confirmDialog = useConfirm();

  const can = (key: string) => session()?.permissions.has(key) ?? false;

  const onDelete = async (role: RoleListItem) => {
    const ok = await confirmDialog({
      title: `Delete role "${role.name}"?`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(role.id);
      toast.success('Role deleted');
    } catch (err) {
      if (err instanceof ApiClientError) {
        const userCount = err.error.details?.userCount;
        toast.error(
          typeof userCount === 'number'
            ? `Role is still assigned to ${userCount} user${userCount === 1 ? '' : 's'} — reassign them first.`
            : err.error.message,
        );
      } else {
        toast.error('Delete failed');
      }
    }
  };

  return (
    <Show when={!roles.isPending} fallback={<Skeleton class="h-40" />}>
      <Show
        when={roles.data}
        fallback={<EmptyState variant="error" title="Failed to load roles" />}
      >
        <Table
          data={roles.data?.data ?? []}
          rowKey={(r) => r.id}
          columns={[
            { key: 'name', header: 'Name', render: (r) => r.name },
            {
              key: 'kind',
              header: '',
              render: (r) =>
                r.isSystem ? <Badge>system</Badge> : <Badge tone="info">custom</Badge>,
            },
            {
              key: 'permissions',
              header: 'Permissions',
              render: (r) => (
                <span
                  class="text-xs text-neutral-600 dark:text-neutral-400"
                  title={r.permissions.join('\n')}
                >
                  {r.permissions.length} key{r.permissions.length === 1 ? '' : 's'}
                </span>
              ),
            },
            {
              key: 'actions',
              header: '',
              align: 'right',
              render: (r) => (
                <div class="flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => props.onEdit(r)}>
                    {/* admin is server-locked; member allows permission edits (UCH-420) */}
                    {can('roles:update') && !(r.isSystem && r.name === 'admin') ? 'Edit' : 'View'}
                  </Button>
                  <Show when={!r.isSystem && can('roles:delete')}>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(r)}>
                      Delete
                    </Button>
                  </Show>
                </div>
              ),
            },
          ]}
          empty={<EmptyState title="No roles" body="System roles are seeded automatically." />}
        />
      </Show>
    </Show>
  );
};
